import React, { useEffect } from "react";
import { IoClose } from "react-icons/io5";

export const ModalCloseButton = ({ onClose, className = "" }) => (
  <button
    type="button"
    onClick={onClose}
    aria-label="Close"
    className={`flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-sm transition-colors hover:bg-gray-50 ${className}`}
  >
    <IoClose className="h-5 w-5" />
  </button>
);

export const ModalBackdrop = ({ onClose, className = "" }) => (
  <div
    role={onClose ? "button" : undefined}
    tabIndex={onClose ? 0 : undefined}
    aria-label={onClose ? "Close modal" : undefined}
    onClick={onClose}
    onKeyDown={
      onClose
        ? (event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onClose();
            }
          }
        : undefined
    }
    className={`fixed inset-0 z-[99998] bg-black/45 backdrop-blur-[2px] modal-backdrop-enter ${
      onClose ? "cursor-pointer" : "pointer-events-none"
    } ${className}`}
  />
);

const useLockBodyScroll = (isOpen) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);
};

/** Full-height sheet for settings, edit forms, login */
export const FullScreenSheet = ({
  isOpen = true,
  onClose,
  children,
  lang,
  headerLeft,
  className = "",
}) => {
  useLockBodyScroll(isOpen);
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex flex-col bg-[#f7f7f8] modal-sheet-enter">
      <div className="mx-auto flex h-full w-full max-w-xl flex-col">
        <div
          className={`flex items-center justify-between px-4 pb-2 pt-4 ${
            lang === "ar" ? "flex-row-reverse" : ""
          }`}
        >
          {headerLeft || <div className="h-10 w-10" />}
          <div className="h-1.5 w-12 rounded-full bg-gray-300" aria-hidden />
          {onClose ? (
            <ModalCloseButton onClose={onClose} />
          ) : (
            <div className="h-10 w-10" />
          )}
        </div>
        <div
          className={`flex-1 overflow-x-hidden overflow-y-auto pb-24 ${className}`}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

/** Centered dialog */
export const CenterModal = ({
  isOpen = true,
  onClose,
  children,
  className = "",
  maxWidth = "max-w-md",
}) => {
  useLockBodyScroll(isOpen);
  if (!isOpen) return null;

  return (
    <>
      <ModalBackdrop onClose={onClose} />
      <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 pointer-events-none">
        <div
          className={`pointer-events-auto w-full ${maxWidth} overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-2xl modal-pop-enter ${className}`}
          role="dialog"
          aria-modal="true"
        >
          {children}
        </div>
      </div>
    </>
  );
};

/** Bottom sheet */
export const BottomSheet = ({
  isOpen = true,
  onClose,
  title,
  children,
  lang,
  className = "",
}) => {
  useLockBodyScroll(isOpen);
  if (!isOpen) return null;

  return (
    <>
      <ModalBackdrop onClose={onClose} />
      <div className="fixed inset-x-0 bottom-0 z-[99999] flex justify-center px-0 modal-sheet-up-enter">
        <div
          className={`w-full max-w-[580px] rounded-t-[28px] border border-gray-100 bg-white shadow-[0_-8px_40px_rgba(0,0,0,0.12)] ${className}`}
          role="dialog"
          aria-modal="true"
        >
          <div className="flex justify-center pt-3">
            <div className="h-1.5 w-12 rounded-full bg-gray-300" />
          </div>
          {(title || onClose) && (
            <div
              className={`flex items-center justify-between gap-3 px-5 pb-2 pt-3 ${
                lang === "ar" ? "flex-row-reverse" : ""
              }`}
            >
              {title ? (
                <h2
                  className={`min-w-0 flex-1 text-[17px] font-semibold leading-snug text-gray-900 ${
                    lang === "ar" ? "text-right" : "text-left"
                  }`}
                >
                  {title}
                </h2>
              ) : (
                <div className="flex-1" />
              )}
              {onClose && <ModalCloseButton onClose={onClose} />}
            </div>
          )}
          <div className="max-h-[80vh] overflow-y-auto px-5 pb-8 pt-2">
            {children}
          </div>
        </div>
      </div>
    </>
  );
};

/** Simple confirm dialog */
export const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel,
  lang,
  variant = "danger",
}) => (
  <CenterModal isOpen={isOpen} onClose={onClose} maxWidth="max-w-sm">
    <div className="p-6">
      <h3
        className={`text-[17px] font-semibold text-gray-900 ${
          lang === "ar" ? "text-right" : "text-left"
        }`}
      >
        {title}
      </h3>
      <p
        className={`mt-2 text-[15px] leading-relaxed text-gray-600 ${
          lang === "ar" ? "text-right" : "text-left"
        }`}
      >
        {message}
      </p>
      <div
        className={`mt-6 flex gap-3 ${lang === "ar" ? "flex-row-reverse" : ""}`}
      >
        <button
          type="button"
          onClick={onClose}
          className="flex-1 rounded-xl border border-gray-200 px-4 py-2.5 text-[14px] font-medium text-gray-700 transition-colors hover:bg-gray-50"
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className={`flex-1 rounded-xl px-4 py-2.5 text-[14px] font-medium text-white transition-colors ${
            variant === "danger"
              ? "bg-gray-900 hover:bg-black"
              : "bg-gray-900 hover:bg-black"
          }`}
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  </CenterModal>
);
